import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Save, ArrowLeft } from 'lucide-react';
import styles from '../CSS/Logo.module.css';
import logos from '../mockdata/Logo';

const LogoSettings = () => {
  const navigate = useNavigate();

  const [headerLogo, setHeaderLogo] = useState(logos.headerLogo);
  const [footerLogo, setFooterLogo] = useState(logos.footerLogo);
  const [headerPreview, setHeaderPreview] = useState(null);
  const [footerPreview, setFooterPreview] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Initialize logos from mockdata
    setHeaderLogo(logos.headerLogo);
    setFooterLogo(logos.footerLogo);
  }, []);

  const handleLogoChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'header') {
          setHeaderPreview(e.target.result);
        } else {
          setFooterPreview(e.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const updateLogosFile = async (newHeaderLogo, newFooterLogo) => {
    const logosFileContent = `
const logos = {
  headerLogo: '${newHeaderLogo}',
  footerLogo: '${newFooterLogo}',
};

export default logos;
    `;
    try {
      await fetch('/update-logos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: logosFileContent }),
      });
    } catch (error) {
      console.error('Error updating logos file:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (headerPreview || footerPreview) {
        const newHeaderLogo = headerPreview || headerLogo;
        const newFooterLogo = footerPreview || footerLogo;

        setHeaderLogo(newHeaderLogo);
        setFooterLogo(newFooterLogo);

        // Simulate updating a backend or file
        await updateLogosFile(newHeaderLogo, newFooterLogo);
      }

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving logos:', error);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>

        {/* Updated header container: Title on the left, Back on the right */}
        <div className={styles.headerContainer}>
          <h1 className={styles.pageTitle}>Logo Settings</h1>
          <button 
            onClick={() => navigate(-1)} 
            className={styles.backButton}
          >
            <ArrowLeft className={styles.backIcon} />
            Back
          </button>
        </div>

        {/* Notifications */}
        {saveStatus === 'success' && (
          <div className={styles.notification}>Changes saved successfully!</div>
        )}
        {saveStatus === 'error' && (
          <div className={`${styles.notification} ${styles.error}`}>
            Error saving logos. Please try again.
          </div>
        )}

        <div className={styles.logoGrid}>
          {/* Header Logo Section */}
          <div className={styles.logoCard}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Header Logo</h2>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.currentLogo}>
                <p className={styles.label}>Current Logo:</p>
                <div className={styles.imageWrapper}>
                  <img
                    src={headerLogo}
                    alt="Current Header Logo"
                    className={styles.logoImage}
                  />
                </div>
              </div>

              {headerPreview && (
                <div className={styles.previewSection}>
                  <p className={styles.label}>Preview:</p>
                  <div className={styles.imageWrapper}>
                    <img
                      src={headerPreview}
                      alt="Header Logo Preview"
                      className={styles.logoImage}
                    />
                  </div>
                </div>
              )}

              <div className={styles.uploadSection}>
                <label className={styles.uploadLabel}>
                  <input
                    type="file"
                    className={styles.fileInput}
                    accept="image/*"
                    onChange={(e) => handleLogoChange(e, 'header')}
                  />
                  <span className={styles.uploadButton}>
                    <Upload className={styles.uploadIcon} />
                    Upload New Header Logo
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer Logo Section */}
          <div className={styles.logoCard}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Footer Logo</h2>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.currentLogo}>
                <p className={styles.label}>Current Logo:</p>
                <div className={styles.imageWrapper}>
                  <img
                    src={footerLogo}
                    alt="Current Footer Logo"
                    className={styles.logoImage}
                  />
                </div>
              </div>

              {footerPreview && (
                <div className={styles.previewSection}>
                  <p className={styles.label}>Preview:</p>
                  <div className={styles.imageWrapper}>
                    <img
                      src={footerPreview}
                      alt="Footer Logo Preview"
                      className={styles.logoImage}
                    />
                  </div>
                </div>
              )}

              <div className={styles.uploadSection}>
                <label className={styles.uploadLabel}>
                  <input
                    type="file"
                    className={styles.fileInput}
                    accept="image/*"
                    onChange={(e) => handleLogoChange(e, 'footer')}
                  />
                  <span className={styles.uploadButton}>
                    <Upload className={styles.uploadIcon} />
                    Upload New Footer Logo
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.saveContainer}>
          <button
            onClick={handleSave}
            className={styles.saveButton}
            disabled={loading}
          >
            <Save className={styles.saveIcon} />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoSettings;
